import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import NotificationSystem from 'react-notification-system';
import get from 'lodash/get';
import { Circle } from 'rc-progress';
import { Row } from 'reactstrap';


import Header from 'components/Header';
import { ButtonLink, Button } from 'components/Button';
import { Grid, Column } from 'components/Layout';
import Modal from 'components/Modal/AnimatedModal';
import Card from 'components/Card';
import Camera from 'components/Icons/Camera';
import File from 'components/Icons/File';
import Envelope from 'components/Icons/Envelope';
import Table from 'components/Icons/Table';

import { parseUrlParams } from 'utils/parseUrlParams';
import { formatCurrency } from 'utils/formatCurrency';

import {
  getSettlements,
  updateCounterOfferStatus,
} from 'actions/settlement';

import {
  getApplications,
  uploadDocuments,
  resendEmail,
} from 'actions/application';

import style from './style.scss';

const EmailApp = (props) => {
  const application = useSelector(state => get(state, 'applications.data.0'));
  const dispatch = useDispatch();
  const params = parseUrlParams(window.location.search);
  const [files, setFiles] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const [isRegradeModalShown, setIsRegradeModalShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptingStatus, setAcceptingStatus] = useState(null);
  const [amountShort, setAmountShort] = useState(null);
  const [percentageShort, setPercentageShort] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isPreQualified = useMemo(() => get(application, 'statusCode') === '5029', [application]);
  const notification = useRef(null);

  const getApplicationsData = (success, fail) => {
    dispatch(getApplications({
      url: `/applications-view/search?sortBy=applicationDate&sortOrder=desc&channelCode=fsl&ApplicationId=${params.applicationId}`,
      success: (res) => {
        success && success(res);
      },
      fail: (error) => {
        fail && fail(error);
      },
    }));
  };

  const getSettlementsData = () => {
    dispatch(getSettlements({
      url: `/fsl-settlements/${params.applicationId}`,
      success: (response) => {
        setIsSubmitted(response.isSubmitted);
        setIsRegradeModalShown(get(response, 'counterOfferStatus') === 'ActionRequired');
        setAmountShort(get(response, 'amountShort'));
        setPercentageShort(get(response, 'percentageShort'));
      },
      fail: () => {
        setIsSubmitted(false);
      },
    }));
  };

  useEffect(() => {
    getApplicationsData();
    getSettlementsData();
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      const filesTemp = [...e.target.files];
      // eslint-disable-next-line
      reader.onload = (event) => {
        setFiles(filesTemp);

        const fileExts = {
          'image/png': '.png',
          'image/jpeg': '.jpg',
          'application/pdf': '.pdf',
        };
        const formData = new FormData();
        formData.append('file', filesTemp[0], `FSL-${params.applicationId}-CDA${fileExts[filesTemp[0].type]}`);
        formData.append('class', 'crb');
        dispatch(uploadDocuments({
          url: `/documents/applications/${params.applicationId}/upload/cda/crb`,
          data: formData,
          // eslint-disable-next-line
          success: (response) => {
            setIsUploaded(true);
          },
          fail: (error) => { // eslint-disable-line
          },
          onUploadProgress: (progressEvent) => {
            setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
          },
        }));
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleResendEmail = () => {
    dispatch(resendEmail({
      url: `/applications/${params.applicationId}/applicant/apply`,
      success: (response) => {
        notification.current
        && notification.current.addNotification({
          message: response.message ? response.message : 'Email has been resent',
          level: 'success',
          position: 'tc',
        });
      },
      fail: (error) => {
        notification.current
        && notification.current.addNotification({
          message: get(error, 'data.message') ? get(error, 'data.message') : 'Error occured',
          level: 'error',
          position: 'tc',
        });
      },
    }));
  };

  const toogleRegradeModal = () => {
    setIsRegradeModalShown(!isRegradeModalShown);
  };

  const handleAccept = (status) => {
    setIsLoading(true);
    setAcceptingStatus(status);
    dispatch(updateCounterOfferStatus({
      url: `fsl-settlements/${params.applicationId}/counter-offer-status?isFundingForm=true`,
      data: `"${status}"`,
      success: () => {
        getApplicationsData(() => {
          setIsLoading(false);
          notification.current
          && notification.current.addNotification({
            message: status === 'Accepted'
              ? 'Counter Offer Accepted'
              : 'Application Status changed to Declined',
            level: 'success',
            position: 'tc',
          });
          toogleRegradeModal();
        });
      },
      fail: (error) => {
        setIsLoading(false);
        notification.current
        && notification.current.addNotification({
          message: get(error, 'message') || 'We encountered an error',
          level: 'error',
          position: 'tc',
        });
        toogleRegradeModal();
      },
    }));
  };

  return (
    <Fragment>
      <Header />
      <NotificationSystem ref={notification} />
      <section className="container section portal">
        <div className="grid-container fluid borrowers-apply">
          <div className="grid-container">
            <Grid className="max-limited">
              <Column className="small-12 large-10 main-column large-offset-1">
                <h3 className={style.noTopBorder}>Action Items</h3>
              </Column>
              <Column className="small-12 large-10 main-column large-offset-1">
                <div className="form-group">
                  <Grid className="grid-margin-x">
                    <Column className="small-12 medium-6">
                      <h5>
                        <b>{`${application && application.applicantName}`}</b><br />
                        Application ID: <b>{params.applicationId}</b> <br />
                      </h5>
                    </Column>
                    <Column className="small-12 medium-6">
                      <h5 className={style.alignRight}>
                        Application Status: <b>{`${application && application.statusLabel}`}</b><br />
                        Settlement Status: <b>{`${application && application.settlementStatus}`}</b>
                      </h5>
                    </Column>
                    <Column className="large-12 small-12">
                      <Grid className="grid-margin-x">
                        <Column className="small-12 large-6">
                          <Card title="Customer Dedicated Account">
                            <Grid className="grid-x card-grid">
                              <ButtonLink
                                className={cn(style['hover-highlight'], style.buttonLink, (application && application.statusCode !== '5029') && cn(style.disabled, style.noBackground))}
                                isDisabled={application && application.statusCode !== '5029'}
                              >
                                <Circle
                                  percent={uploadingPercentage}
                                  strokeWidth="4"
                                  strokeColor="#04bc6c"
                                  trailColor="#002248"
                                  strokeLinecap="butt"
                                  className={style.progress}
                                />
                                <span><Camera className={style.icon} width="35" /></span>
                                <h5>{ isUploaded ? 'Uploaded Screenshot' : 'Upload Screenshot' }</h5>
                                <p className="p-small">{files && files.length ? files.map(item => item.name) : 'Customer Dedicated Account'}</p>
                                <input
                                  type="file"
                                  id="file"
                                  name="ff_file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  style={{ opacity: 0, overflow: 'hidden', position: 'absolute', zIndex: '-1', width: '0.1px', height: '0.1px' }}
                                  onChange={handleFileChange}
                                  disabled={application && application.statusCode !== '5029'}
                                />
                                <label className={cn('input-group-btn', style.label)} htmlFor="file">
                                  <div className={cn('button green w-100', style.button, (application && application.statusCode !== '5029') && 'disabled')}>Select A File...</div>
                                </label>
                              </ButtonLink>
                            </Grid>
                          </Card>
                        </Column>

                        <Column className="small-12 large-6">
                          <Card title="Funding Form" isChecked={isSubmitted}>
                            <Grid className="card-grid">
                              <ButtonLink
                                className={cn(style['hover-highlight'], style.buttonLink)}
                                onClick={() => {
                                  props.history.push({
                                    pathname: '/dashboard/dsc',
                                    search: `?applicationId=${params.applicationId}`,
                                    state: { applicationDetail: application },
                                  });
                                }}
                                isDisabled={!isPreQualified}
                              >
                                <span><Table className={style.icon} width="35" /></span>
                                <h5>Funding Form</h5>
                                <p className="p-small">Complete Funding Form</p>
                              </ButtonLink>
                            </Grid>
                          </Card>
                        </Column>
                        <Column className="small-12 large-6">
                          <Card title="Certificate of Settlement">
                            <Grid className="card-grid">
                              <ButtonLink
                                className={cn(style['hover-highlight'], style.buttonLink, (application && application.statusName !== 'Funded') && style.disabled)}
                                onClick={() => { props.history.push(`/dashboard/settlement?applicationId=${params.applicationId}`); }}
                                isDisabled={application && application.statusName !== 'Funded'}
                              >
                                <span><File width="40" /></span>
                                <h5>Certificate of Settlement</h5>
                                <p className="p-small">Complete Certificate of Settlement</p>
                              </ButtonLink>
                            </Grid>
                          </Card>
                        </Column>
                        <Column className="small-12 large-6">
                          <Card title="Resend Email">
                            <Grid className="card-grid">
                              <ButtonLink
                                className={cn(style['hover-highlight'], style.buttonLink, style.upload, style.resend)}
                              >
                                <span><Envelope className={style.icon} width="35" /></span>
                                <ButtonLink
                                  className={cn('button green', style.button, style.sendEmail, style.upload)}
                                  onClick={handleResendEmail}
                                >
                                  Email to Borrower
                                </ButtonLink>
                                {/* <p className="p-small">Resend Email to Borrower</p> */}
                              </ButtonLink>
                            </Grid>
                          </Card>
                        </Column>
                      </Grid>
                    </Column>
                  </Grid>
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      </section>

      <Modal
        className="sm-modal"
        isShown={isRegradeModalShown}
        onClose={toogleRegradeModal}
      >
        <Row>
          <p className="text-center">
            The Requested Amount cannot be approved. However, we can approve a lower amount. Please click below to ACCEPT this counter offer and make this adjustment to the Funding Form.
          </p>
        </Row>
        <div className={style.priceContainer}>
          <div className={style.priceItem}>
            <div>Requested Amount</div>
            <div>${formatCurrency(get(application, 'requestedAmount', 0), 2)}</div>
          </div>
          <div className={style.maxApprovalAmount}>
            <div>${formatCurrency(get(application, 'maxApprovalAmount', 0), 2)}</div>
            <div>Max Approval Amount</div>
          </div>
          <div className={style.priceItem}>
            <div>Amount Short</div>
            <div>${amountShort ? formatCurrency(amountShort, 2) : ' -'}</div>
          </div>
          <div className={style.priceItem}>
            <div>Percentage Short</div>
            <div>{percentageShort ? formatCurrency(percentageShort, 2) : ' -'}%</div>
          </div>
        </div>
        <Button
          className="button green arrow w-100"
          isLoading={isLoading && acceptingStatus === 'Accepted'}
          onClick={() => (handleAccept('Accepted'))}
          isDisabled={isLoading && acceptingStatus === 'CannotProceed'}
        >
          ACCEPT
        </Button>
        <Button
          className="button arrow w-100"
          isLoading={isLoading && acceptingStatus === 'CannotProceed'}
          onClick={() => (handleAccept('CannotProceed'))}
          isDisabled={isLoading && acceptingStatus === 'Accepted'}
        >
          Cannot Proceed
        </Button>
      </Modal>
    </Fragment>
  );
};

EmailApp.propTypes = {
  history: PropTypes.object.isRequired,
};

EmailApp.defaultProps = {

};

export default withRouter(EmailApp);



// WEBPACK FOOTER //
// ./src/containers/pages/TodoList/index.js